import type { CertGenParams } from '@yandex-int/k-common';
import type { IntlShape } from 'react-intl';

interface Spring2023MathOlympParams extends CertGenParams {
    intl: IntlShape;
}

export const GLMathSpring2023 = ({
    school_name: schoolName,
    first_name: firstName,
    middle_name: middleName,
    last_name: lastName,
    intl,
}: Spring2023MathOlympParams) => {
    const moskvichev = intl.formatMessage({ id: 'teacherLK.card.certificate.template.signature.moskvichev.light' });
    const yashchenko = intl.formatMessage({ id: 'teacherLK.card.certificate.template.signature.yashchenko.light' });
    const bgImgSrc = intl.formatMessage({ id: 'teacherLK.card.certificate.spring_2023_math.background' });

    return `
        <!doctype html>
            <html lang="ru" xmlns="http://www.w3.org/1999/html">
            <head>
                <meta charset="utf-8"/>
                <meta name="description" content=""/>
                <meta name="keywords" content=""/>
                <meta name="viewport" content="width=device-width, maximum-scale=1.0, initial-scale=1.0"/>
                <style type="text/css">
                    /*
                    //-----------------------------------------
                    // Fonts
                    //-----------------------------------------
                    */

                    @font-face {
                        font-family: 'YS Text';
                        src: url("https://yastatic.net/s3/home/fonts/ys/1/text-regular.woff2") format("woff2"),
                        url("https://yastatic.net/s3/home/fonts/ys/1/text-regular.woff") format("woff");
                        font-weight: 400;
                        font-style: normal;
                    }

                    /*
                    //-----------------------------------------
                    // Print specifics
                    //-----------------------------------------
                    */

                    @page {
                        size: 210mm 297mm;
                        margin: 0;
                    }

                    @page {
                        margin: 0;
                    }

                    /*
                    //-----------------------------------------
                    // Scaffolding
                    //-----------------------------------------
                    */

                    html, body {
                        margin: 0;
                        padding: 0;
                        width: 100%;
                        height: 100%;
                    }

                    body {
                        font-size: calc(12 * 297mm / 895);
                        font-weight: 400;
                        font-family: 'YS Text';
                        line-height: calc(16 * 297mm / 895);
                        color: #000;
                        min-height: 100%;
                        height: auto !important;
                        height: 100%;
                        background: #FFFFFF;
                        -webkit-text-size-adjust: 100%;
                    }

                    * {
                        box-sizing: border-box;
                    }

                    /*
                    //-----------------------------------------
                    // Certificate
                    //-----------------------------------------
                    */

                    .page {
                        position: relative;
                        width: 210mm;
                        height: 297mm;
                        overflow: hidden;
                        page-break-after: always;
                        break-after: page;
                        padding: calc(30 * 297mm / 895) calc(30 * 297mm / 895);

                        background-image: url("${bgImgSrc}");
                        background-repeat: no-repeat;
                        background-position: 0 0;
                        background-size: 100% 100%;
                    }

                    .logo {
                        width: calc(197 * 297mm / 895);
                        height: calc(24 * 297mm / 895);
                    }

                    .title
                    {
                        font-size: calc(32 * 297mm / 895);
                        line-height: calc(38 * 297mm / 895);

                        margin-top: calc(32 * 297mm / 895);
                        margin-bottom: calc(8 * 297mm / 895);
                    }

                    .name {
                        font-size: calc(27 * 297mm / 895);
                        line-height: calc(32 * 297mm / 895);

                        width: calc(325 * 297mm / 895);
                        margin-top: calc(71 * 297mm / 895);
                    }

                    .school {
                        margin-top: calc(12 * 297mm / 895);
                    }

                    .description {
                        font-size: calc(13.6471 * 297mm / 895);
                        line-height: calc(16 * 297mm / 895);

                        margin-top: calc(32 * 297mm / 895);
                    }

                    .cpm
                    {
                        position: absolute;
                        top: calc(39 * 297mm / 895);
                        left: calc(434 * 297mm / 895);
                        width: calc(116 * 297mm / 895);
                        height: calc(36 * 297mm / 895);
                    }

                    .date {
                        position: absolute;
                        top: calc(138 * 297mm / 895);
                        left: calc(434 * 297mm / 895);

                        color: #ffffff;
                    }

                    .signature {
                        position: absolute;
                        color: #ffffff;
                    }

                    .signature-moskvichev {
                        top: calc(207 * 297mm / 895);
                        left: calc(434 * 297mm / 895);
                    }

                    .signature-yashchenko {
                        top: calc(368 * 297mm / 895);
                        left: calc(434 * 297mm / 895);
                    }

                    .signature-moskvichev .signature__img {
                        width: calc(56 * 297mm / 895);
                        height: calc(84 * 297mm / 895);
                    }

                    .signature-yashchenko .signature__img {
                        width: calc(51 * 297mm / 895);
                        height: calc(45 * 297mm / 895);
                    }

                    .signature__name {
                        font-size: 10pt;
                        line-height: 11pt;
                        letter-spacing: 0;
                        text-align: left;
                    }

                    .signature__position {
                        font-size: 8pt;
                        line-height: 11pt;
                        letter-spacing: 0;
                        text-align: left;

                        margin-top: calc(8 * 297mm / 895);
                    }

                </style>
            </head>
            <body>
                <div class="page">
                    <img class="logo"
                         src="https://e7n.s3.yandex.net/static/cert-inf-2022-spring-olymp/logo.svg"
                         alt="Лого Яндекс">
                    <div class="title">
                        Благодарственное
                        <br/>
                        письмо
                    </div>
                    <div>за участие в олимпиаде «Я люблю математику»</div>
                    <div class="name">
                        ${firstName || ''} ${middleName || ''} ${lastName || ''}
                    </div>
                    <div class="school">
                        ${schoolName || ''}
                    </div>
                    <div class="description">
                        Уважаемый учитель!<br>
                        Благодарим Вас за вклад в развитие<br>
                        олимпиадного движения и участие Ваших<br>
                        учеников в олимпиаде «Я люблю математику»<br>
                        от Яндекс Учебника.
                    </div>
                    <img alt="Центр Педагогического Мастерства"
                        class="cpm"
                        src="https://avatars.mds.yandex.net/get-schoolbook-images/5623482/xXDgP8LE07G0hlX2VTSvX/orig">
                    <div class="date">
                        Москва, 2023
                    </div>
                    <div class="signature signature-moskvichev">
                        <img class="signature__img"
                             src="${moskvichev}"
                             alt="Подпись A. М. Москвичев">
                        <div class="signature__name">
                            А. М. Москвичев
                        </div>
                        <div class="signature__position">
                            Руководитель сервиса<br>
                            Яндекс Учебник
                        </div>
                    </div>
                    <div class="signature signature-yashchenko">
                        <img class="signature__img"
                             src="${yashchenko}"
                             alt="Подпись И. В. Ященко">
                        <div class="signature__name">
                            И. В. Ященко
                        </div>
                        <div class="signature__position">
                            Председатель оргкомитета<br>
                            олимпиады, директор Центра<br>
                            педагогического мастерства
                        </div>
                    </div>
                </div>
            </body>
        </html>
    `;
};
